// Sidebar / drawer IA per account type. Pure (unit-tested in navConfig.test.js).
// Every entry links to a page that exists in App.js, or to a live www.brxs.ca
// booking flow (external: true). Pages that don't exist yet are not listed here.
import { workspaceKind } from "./workspace";
import { propertyCopy } from "./clientTypes";

export const BOOK_SHOOT_URL = "https://www.brxs.ca/book";
export const BOOK_SITE_URL = "https://www.brxs.ca/book/site";
// Optional home services (staging, cleaning, inspections, measurement; repairs paused).
// Quote request only: no prices, nothing booked until the client approves.
export const REQUEST_QUOTE_URL = "https://www.brxs.ca/services";

// www /book link that starts the booking already filled in: the listing's
// address (from a deal) and the signed-in agent's own name, email, phone and
// brokerage, so the paid shoot lands back on this listing (matched by agent
// email + street line). Only non-empty values are sent; nothing else.
export function bookShootHref({ address, city, province, user } = {}) {
  const clean = (v, max = 160) => String(v == null ? "" : v).replace(/\s+/g, " ").trim().slice(0, max);
  const q = new URLSearchParams();
  const street = clean(address);
  if (street) {
    const tail = [city, province].map((v) => clean(v, 60)).filter(Boolean)
      .filter((v) => !street.toLowerCase().includes(v.toLowerCase()));
    q.set("address", [street, ...tail].join(", "));
  }
  const fields = {
    name: user?.full_name || user?.name,
    email: user?.email,
    phone: user?.phone,
    brokerage: user?.brokerage_name || user?.company_name,
  };
  Object.entries(fields).forEach(([k, v]) => { const c = clean(v, 120); if (c) q.set(k, c); });
  const qs = q.toString();
  return qs ? `${BOOK_SHOOT_URL}?${qs}` : BOOK_SHOOT_URL;
}

const item = (icon, label, to, extra = {}) => ({ icon, label, to, ...extra });

const OVERVIEW = (to) => item("overview", "Overview", to);
const DEALS = item("deal", "Transactions", "/transactions");
const CONTACTS = item("crm", "Contacts", "/crm");
const CALENDAR = item("calendar", "Calendar", "/calendar");
const MLS = item("mls", "MLS listings", "/mls");
const DOCUMENTS = item("docs", "Documents", "/documents");
const ESIGN = item("esign", "E-signatures", "/esign");
const FORMS = item("folder", "Forms", "/forms");
const COMPLIANCE = item("compliance", "Compliance", "/compliance");
const SHOOTS = item("media", "Shoots", "/jobs");
const LIBRARY = (to) => item("layers", "Media library", to, { match: [to, "/properties"] });
const PHOTOGRAPHERS = item("person", "Photographers", "/vendors");
const BOOK_SHOOT = item("booked", "Book media", BOOK_SHOOT_URL, { external: true });
const BOOK_SITE = item("booked", "Book a site visit", BOOK_SITE_URL, { external: true });
// Agents list homes, so their quote entry opens on staging (the form also
// offers cleaning); property accounts land on the full /services page.
const REQUEST_QUOTE = item("homeServices", "Request a quote", REQUEST_QUOTE_URL, { external: true });
const REQUEST_QUOTE_AGENT = item("homeServices", "Staging or cleaning quote", `${REQUEST_QUOTE_URL}#staging`, { external: true });
const ACCOUNTING = item("ledger", "Accounting", "/accounting");
const REPORTS = item("report", "Reports", "/reports");
const BILLING = (to) => item("card", "Billing", to);
const INTEGRATIONS = item("integrations", "Connected tools", "/integrations");
const SETTINGS = item("settings", "Settings", "/settings");

const KIND_LABEL = {
  platform: "Bricks admin",
  brokerage_manager: "Brokerage",
  agent: "Brokerage agent",
  team_lead: "Team",
  team_member: "Team",
  solo: "Individual",
  builder: "Builder",
  reit_leasing: "REIT",
  investor: "Investor",
  commercial: "Commercial",
  property_manager: "Property manager",
  pending: "Getting started",
  vendor: "Photographer",
};

export function navFor(user) {
  const kind = workspaceKind(user);
  const label = KIND_LABEL[kind] || "Account";
  const name =
    user?.company_name || user?.brokerage_name || user?.team_name || user?.name || "Bricks";

  let sections;
  let primary = { label: "Book media", href: bookShootHref({ user }) };

  switch (kind) {
    case "platform":
      primary = null;
      sections = [
        { label: null, items: [OVERVIEW("/owner")] },
        { label: "Operations", items: [
          item("inbox", "Bookings", "/bookings"),
          item("siteProgress", "Site progress", "/site-progress"),
          item("prospect", "Prospectors", "/prospectors"),
          item("trend", "Outreach", "/outreach"),
          item("hiring", "Hiring", "/hiring"),
        ] },
        { label: "Company", items: [item("docs", "Internal", "/internal"), INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "builder":
    case "reit_leasing":
    case "investor":
    case "commercial":
    case "property_manager": {
      const c = propertyCopy(kind);
      primary = { label: c.book.label, href: c.book.href };
      const work = [item("siteProgress", c.things, "/developer/projects")];
      work.push(kind === "builder" ? BOOK_SITE : item("booked", c.book.label, c.book.href, { external: /^https?:/.test(c.book.href) }));
      if (c.siteProgress) work.push(BOOK_SITE);
      work.push(REQUEST_QUOTE);
      sections = [
        { label: null, items: [OVERVIEW("/developer/overview")] },
        { label: kind === "builder" ? "Site progress" : c.things, items: work },
        { label: "Account", items: [BILLING("/developer/billing"), SETTINGS] },
      ];
      break;
    }
    case "brokerage_manager":
      sections = [
        { label: null, items: [OVERVIEW("/overview")] },
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR, MLS] },
        { label: "Documents", items: [DOCUMENTS, ESIGN, FORMS, COMPLIANCE] },
        { label: "Media", items: [SHOOTS, LIBRARY("/properties"), PHOTOGRAPHERS, BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Finance", items: [ACCOUNTING, REPORTS, BILLING("/billing")] },
        { label: "Growth", items: [item("boost", "Boost", "/boost"), item("recruit", "Recruit", "/recruit")] },
        { label: "Office", items: [item("team", "Agents", "/agents"), INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "team_lead":
      sections = [
        { label: null, items: [OVERVIEW("/team/overview")] },
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR] },
        { label: "Documents", items: [ESIGN] },
        { label: "Media", items: [SHOOTS, LIBRARY("/team/properties"), PHOTOGRAPHERS, BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Team", items: [item("team", "Members", "/team/members"), BILLING("/team/billing"), INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "team_member":
      sections = [
        { label: null, items: [OVERVIEW("/team/overview")] },
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR] },
        { label: "Documents", items: [ESIGN] },
        { label: "Media", items: [SHOOTS, LIBRARY("/team/properties"), PHOTOGRAPHERS, BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Account", items: [INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "solo":
      sections = [
        { label: null, items: [OVERVIEW("/solo/overview")] },
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR] },
        { label: "Documents", items: [ESIGN] },
        { label: "Media", items: [SHOOTS, LIBRARY("/solo/properties"), BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Account", items: [BILLING("/solo/billing"), INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "pending":
      sections = [
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR] },
        { label: "Documents", items: [ESIGN] },
        { label: "Media", items: [SHOOTS, BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Account", items: [INTEGRATIONS, SETTINGS] },
      ];
      break;
    case "vendor":
      primary = null;
      sections = [];
      break;
    case "agent":
    default:
      sections = [
        { label: null, items: [OVERVIEW("/overview")] },
        { label: "Desk", items: [DEALS, CONTACTS, CALENDAR] },
        { label: "Documents", items: [DOCUMENTS, ESIGN, COMPLIANCE] },
        { label: "Media", items: [SHOOTS, LIBRARY("/properties"), BOOK_SHOOT, REQUEST_QUOTE_AGENT] },
        { label: "Account", items: [INTEGRATIONS, SETTINGS] },
      ];
  }
  return { kind, label, name, sections, primary };
}

export function isActive(entry, pathname) {
  if (entry.external) return false;
  const roots = entry.match || [entry.to];
  return roots.some((r) => pathname === r || pathname.startsWith(r + "/"));
}

// Page title for the top bar: the active nav entry, else a known detail page.
const EXTRA_TITLES = [
  ["/transactions/", "Transaction"],
  ["/properties/", "Property"],
  ["/hiring/", "Applicant"],
];

export function titleFor(nav, pathname) {
  for (const s of nav.sections) {
    for (const e of s.items) {
      if (!e.external && (pathname === e.to)) return e.label;
    }
  }
  const extra = EXTRA_TITLES.find(([p]) => pathname.startsWith(p));
  if (extra) return extra[1];
  for (const s of nav.sections) {
    for (const e of s.items) {
      if (isActive(e, pathname)) return e.label;
    }
  }
  return "";
}

/** Flat list of in-app routes the nav exposes (tests use this). */
export function navRoutes(nav) {
  return nav.sections.flatMap((s) => s.items.filter((e) => !e.external).map((e) => e.to));
}
